/**
 * Modal para crear / editar un `AuditPlotPlanting` desde el web.
 * Fase C del refactor de doblados. La edición completa (con selects
 * de catálogo cargados desde useSelect Refine) queda simple: sólo los
 * campos que suele tocar el admin/coord tras la auditoría (variedad,
 * marco, plantas, superficie, año). Los demás quedan como estaban.
 */

import { useEffect } from "react";
import {
  App as AntdApp,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Col,
  Select,
} from "antd";
import { useList } from "@refinedev/core";
import type {
  AuditPlotPlanting,
  AuditPlotPlantingWritePayload,
} from "@/types/audit";
import { auditPlotPlantingService } from "@/services/api/auditPlotPlantingService";

interface CatalogueOption {
  id: number;
  name: string;
  code?: string;
}

interface Props {
  auditPlotId: string;
  /** Planting a editar. Null → creación nueva. */
  planting: AuditPlotPlanting | null;
  /** ordering sugerido para nuevas (max existente + 1). */
  suggestedOrdering: number;
  onDone: (updated: AuditPlotPlanting) => void;
  onCancel: () => void;
}

export const PlantingEditModal: React.FC<Props> = ({
  auditPlotId,
  planting,
  suggestedOrdering,
  onDone,
  onCancel,
}) => {
  const { message } = AntdApp.useApp();
  const [form] = Form.useForm();

  const isEdit = planting != null;

  // Catálogos — Refine V5 devuelve `{ result: {data, total}, query }`.
  const { result: varietiesRes } = useList<CatalogueOption>({
    resource: "varieties",
    pagination: { pageSize: 200 },
  });
  const { result: patternsRes } = useList<CatalogueOption>({
    resource: "patterns",
    pagination: { pageSize: 200 },
  });
  const { result: plotTypesRes } = useList<CatalogueOption>({
    resource: "plot-types",
    pagination: { pageSize: 200 },
  });
  const { result: intermediateRes } = useList<CatalogueOption>({
    resource: "intermediate-woods",
    pagination: { pageSize: 200 },
  });

  useEffect(() => {
    if (planting) {
      form.setFieldsValue({
        ordering: planting.ordering,
        variety: planting.variety,
        pattern: planting.pattern,
        plot_type: planting.plot_type,
        intermediate_wood: planting.intermediate_wood,
        crop_date: planting.crop_date,
        plantation_frame_row: planting.plantation_frame_row,
        plantation_frame_column: planting.plantation_frame_column,
        plants: planting.plants,
        grafting: planting.grafting,
        surface: planting.surface,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({ ordering: suggestedOrdering });
    }
  }, [planting, suggestedOrdering, form]);

  const handleOk = async () => {
    let values;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const payload: AuditPlotPlantingWritePayload = {
      ordering: values.ordering,
      variety: values.variety,
      plot_type: values.plot_type ?? null,
      pattern: values.pattern ?? null,
      intermediate_wood: values.intermediate_wood ?? null,
      crop_date: values.crop_date ?? null,
      plantation_frame_row: values.plantation_frame_row ?? null,
      plantation_frame_column: values.plantation_frame_column ?? null,
      plants: values.plants ?? null,
      grafting: values.grafting ?? null,
      surface: values.surface ?? null,
    };
    try {
      let saved: AuditPlotPlanting;
      if (isEdit && planting) {
        saved = await auditPlotPlantingService.update(planting.id, payload);
        message.success("Plantación actualizada.");
      } else {
        saved = await auditPlotPlantingService.create({
          ...payload,
          audit_plot: auditPlotId,
        });
        message.success("Plantación creada.");
      }
      onDone(saved);
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } }).response?.data
          ?.detail ?? (err as Error).message;
      message.error(`Error: ${detail}`);
    }
  };

  const varieties = varietiesRes?.data ?? [];
  const patterns = patternsRes?.data ?? [];
  const plotTypes = plotTypesRes?.data ?? [];
  const intermediates = intermediateRes?.data ?? [];

  const currentYear = new Date().getFullYear();

  return (
    <Modal
      open
      title={
        isEdit
          ? `Editar plantación #${planting?.ordering ?? ""}`
          : "Nueva plantación"
      }
      onCancel={onCancel}
      onOk={handleOk}
      okText="Guardar"
      cancelText="Cancelar"
      width={640}
    >
      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              name="ordering"
              label="Orden"
              rules={[
                { required: true, message: "Requerido" },
                { type: "number", min: 1 },
              ]}
              tooltip="1 = principal. Las plantaciones extras van con 2, 3…"
            >
              <InputNumber min={1} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={16}>
            <Form.Item
              name="variety"
              label="Variedad"
              rules={[{ required: true, message: "Requerido" }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={varieties.map((v) => ({
                  value: v.id,
                  label: v.code ? `${v.code} · ${v.name}` : v.name,
                }))}
                placeholder="Seleccionar"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="pattern" label="Patrón">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={patterns.map((p) => ({
                  value: p.id,
                  label: p.name,
                }))}
                placeholder="Sin patrón"
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="plot_type" label="Tipo de plantación">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={plotTypes.map((p) => ({
                  value: p.id,
                  label: p.name,
                }))}
                placeholder="Sin tipo"
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={8}>
            <Form.Item
              name="crop_date"
              label="Año de plantación"
              rules={[
                { type: "number", min: 1900, max: currentYear + 5 },
              ]}
            >
              <InputNumber
                min={1900}
                max={currentYear + 5}
                style={{ width: "100%" }}
                placeholder="p.ej. 2020"
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="plantation_frame_row" label="Marco fila (m)">
              <InputNumber
                min={0}
                step={0.1}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="plantation_frame_column"
              label="Marco columna (m)"
            >
              <InputNumber
                min={0}
                step={0.1}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={12}>
          <Col span={8}>
            <Form.Item name="plants" label="Nº plantas">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="surface" label="Superficie (ha)">
              <InputNumber
                min={0}
                step={0.0001}
                precision={4}
                style={{ width: "100%" }}
              />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="grafting" label="Nº injertos">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="intermediate_wood" label="Madera intermedia">
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            options={intermediates.map((i) => ({
              value: i.id,
              label: i.name,
            }))}
            placeholder="Sin madera intermedia"
          />
        </Form.Item>

        {isEdit && planting?.sub_geom ? (
          <div style={{ background: "#fff7e6", padding: 8, borderRadius: 4 }}>
            <Input.TextArea
              readOnly
              rows={2}
              value="Esta plantación tiene sub-geometría (doblado parcial). Se edita desde la app móvil."
            />
          </div>
        ) : null}
      </Form>
    </Modal>
  );
};
